import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import PageGuide, { StatusKey } from '../components/PageGuide';
import { ClipButton } from '../components/ReportWorkspace';
import '../styles/HomeBrief.css';
import '../styles/NewDashboard.css';
import filterParams from '../utils/filterParams';
import { brandStatus } from '../utils/evidenceStatus';

// Brand feedback groups are the shared brand status, under this page's labels.
const GROUP_FOR_STATUS = { red: 'review', amber: 'monitor', green: 'positive', grey: 'limited' };

export default function MarketBrief({ filters, setLoading }) {
  const [data, setData] = useState({
    summary: {},
    trends: [],
    fields: [],
    issues: [],
    brands: [],
    regions: [],
  });
  const [busy, setBusy] = useState(true);
  const [brandGroup, setBrandGroup] = useState('monitor');
  useEffect(() => {
    const controller = new AbortController();
    const params = filterParams(filters);
    setBusy(true);
    setLoading(true);
    Promise.all([
      axios.get(`/api/home/summary?${params}`, { signal: controller.signal }),
      axios.get(`/api/decision/region-crop?${params}`, { signal: controller.signal }),
      axios.get(`/api/decision/brands?${params}`, { signal: controller.signal }),
      axios.get(`/api/agent-view/insights?${params}`, { signal: controller.signal }),
    ])
      .then(([home, field, brand, team]) =>
        setData({
          summary: home.data.summary || {},
          trends: home.data.trends || [],
          fields: field.data.cells || [],
          issues: field.data.issues || [],
          brands: brand.data.brands || [],
          regions: team.data.regions || [],
        }),
      )
      .catch((error) => {
        if (!axios.isCancel(error))
          setData((previous) => ({ ...previous, error: 'The management view could not be loaded.' }));
      })
      .finally(() => {
        setBusy(false);
        setLoading(false);
      });
    return () => controller.abort();
  }, [filters, setLoading]);

  const fieldChanges = useMemo(
    () => data.fields.filter((item) => item.lifecycle === 'emerging' && item.conversations >= 3).slice(0, 4),
    [data.fields],
  );
  const brandGroups = useMemo(() => {
    const groups = { review: [], monitor: [], positive: [], limited: [] };
    data.brands.forEach((item) => {
      groups[GROUP_FOR_STATUS[brandStatus(item)]].push(item);
    });
    return groups;
  }, [data.brands]);
  const brandReviews = brandGroups.review;
  const fieldReason = (item) => {
    const matches = data.issues
      .filter(
        (x) =>
          x.region_id === item.region_id && String(x.crop).toLowerCase() === String(item.crop).toLowerCase(),
      )
      .sort((a, b) => Number(b.conversations || 0) - Number(a.conversations || 0));
    if (matches.length) return `${matches[0].issue} mentioned in ${matches[0].conversations} conversations`;
    if (Number(item.need_signals || 0))
      return `${item.need_signals} issue-related mentions; open Field Intelligence to identify the concern`;
    return 'Mention frequency rose; no specific issue is classified yet';
  };
  const maxRegion = Math.max(1, ...data.regions.map((item) => Number(item.conversations || 0)));
  const clipBase = {
    page: 'Market Brief',
    subtitle: 'Selected dashboard period',
    finding:
      'Review recording volume and source conversations before treating a movement as a market change.',
  };

  return (
    <div className="v2-page">
      <div className="module-header">
        <div className="module-kicker">MANAGEMENT VIEW</div>
        <h2>Market Brief</h2>
        <p>What field conversations show, what changed, and where the team has enough coverage to act.</p>
      </div>
      <FilterBar />
      <PageGuide
        purpose="Start here for the market brief. Open Field, Brand or Field Team for the details and supporting conversations."
        steps={[
          'Check recording coverage and its timeline.',
          'Review field changes and brand concerns.',
          'Open the relevant page to examine reasons and source conversations.',
        ]}
        metrics={[
          { name: 'Conversations', definition: 'Distinct field conversations in the selected period.' },
          {
            name: 'Active field team',
            definition: 'Field team members with at least one included conversation.',
          },
          {
            name: 'Items to review',
            definition:
              'Emerging crop/location combinations with at least three conversations plus brands with negative or declining evidence.',
          },
        ]}
      />
      <div className="home-brief-head">
        <h3>Market brief</h3>
        <StatusKey />
      </div>
      {busy ? (
        <div className="decision-empty">Loading market brief…</div>
      ) : data.error ? (
        <div className="decision-empty">{data.error}</div>
      ) : (
        <>
          <section className="decision-pane home-activity">
            <header>
              <div>
                <h3>Conversation activity</h3>
                <p>
                  Read movement alongside recording volume; a change may reflect where the team recorded more
                  calls.
                </p>
              </div>
              <ClipButton
                item={{
                  ...clipBase,
                  title: 'Conversation activity',
                  visual: 'timeline',
                  timeline: data.trends.map((x) => ({
                    period: x.week_start,
                    conversations: Number(x.conversations || 0),
                  })),
                }}
              />
            </header>
            <div className="home-activity-summary">
              <span>
                <b>{data.summary.conversation_count || 0}</b> conversations
              </span>
              <span>
                <b>{data.summary.agent_count || 0}</b> active field team
              </span>
              <span>
                <b>{fieldChanges.length + brandReviews.length}</b> items to review
              </span>
            </div>
            <Plot
              data={[
                {
                  x: data.trends.map((item) => item.week_start),
                  y: data.trends.map((item) => Number(item.conversations || 0)),
                  type: 'scatter',
                  mode: 'lines+markers',
                  fill: 'tozeroy',
                  fillcolor: 'rgba(31,94,255,.09)',
                  line: { color: '#1f5eff', width: 3 },
                },
              ]}
              layout={{
                height: 270,
                margin: { t: 10, r: 15, b: 40, l: 42 },
                xaxis: { title: 'Week' },
                yaxis: { title: 'Conversations', rangemode: 'tozero' },
                paper_bgcolor: '#fffdf8',
                plot_bgcolor: '#fffdf8',
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
            />
          </section>
          <div className="home-brief-grid">
            <section className="decision-pane">
              <header>
                <div>
                  <h3>Field changes to check</h3>
                  <p>What is being mentioned more often, and the recorded reason where available.</p>
                </div>
                <ClipButton
                  item={{
                    ...clipBase,
                    title: 'Field changes to check',
                    visual: 'table',
                    rows: fieldChanges.map((x) => ({
                      label: `${x.crop} in ${x.region_name}`,
                      value: fieldReason(x),
                      status: 'amber',
                    })),
                  }}
                />
              </header>
              <div className="home-brief-list">
                {fieldChanges.length ? (
                  fieldChanges.map((item) => (
                    <div key={`${item.region_id}-${item.crop}`}>
                      <b>
                        {item.crop} in {item.region_name}
                      </b>
                      <span>{fieldReason(item)}</span>
                      <small>
                        {item.conversations} conversations · {item.contributors} contributors ·{' '}
                        {item.change_percent == null ? 'newly mentioned' : `up ${item.change_percent}%`}
                      </small>
                    </div>
                  ))
                ) : (
                  <p>No emerging field topic meets the current minimum volume.</p>
                )}
              </div>
            </section>
            <section className="decision-pane">
              <header>
                <h3>Brand feedback</h3>
                <p>
                  Select a category to see the brands behind it. These are conversation-based groups, not
                  campaign targets.
                </p>
              </header>
              <div className="home-brand-groups">
                {[
                  ['review', 'Negative'],
                  ['monitor', 'Monitor'],
                  ['positive', 'Positive'],
                  ['limited', 'Not enough'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`${key} ${brandGroup === key ? 'active' : ''}`}
                    onClick={() => setBrandGroup(key)}
                  >
                    <span>{label}</span>
                    <i>
                      <b
                        style={{
                          width: `${Math.max(4, (brandGroups[key].length * 100) / Math.max(1, data.brands.length))}%`,
                        }}
                      />
                    </i>
                    <strong>{brandGroups[key].length}</strong>
                  </button>
                ))}
              </div>
              <div className="home-brief-list">
                {brandGroups[brandGroup].slice(0, 5).map((item) => (
                  <div key={item.brand}>
                    <b>{item.brand}</b>
                    <span>
                      {item.total} scored conversations · net sentiment {item.net_sentiment > 0 ? '+' : ''}
                      {item.net_sentiment}% · {item.main_context || 'No crop context'}
                    </span>
                    <small>
                      {brandGroup === 'monitor' && item.lifecycle === 'dying'
                        ? 'Mentions declined in the later period'
                        : brandGroup === 'limited'
                          ? 'Fewer than five scored conversations'
                          : brandGroup === 'review'
                            ? 'More negative than positive assessments'
                            : 'More positive than negative assessments'}
                    </small>
                  </div>
                ))}
                {!brandGroups[brandGroup].length && (
                  <p>No brands in this category for the selected period.</p>
                )}
              </div>
            </section>
          </div>
          <section className="decision-pane home-coverage">
            <header>
              <div>
                <h3>Where conversations came from</h3>
                <p>
                  Coverage by province. Use Field Team to see who contributed and whether coverage is broad
                  enough.
                </p>
              </div>
              <ClipButton
                item={{
                  ...clipBase,
                  title: 'Where conversations came from',
                  visual: 'bars',
                  rows: data.regions.slice(0, 8).map((x) => ({
                    label: x.region,
                    value: `${x.conversations} conversations`,
                    status: 'none',
                  })),
                }}
              />
            </header>
            <div>
              {data.regions.slice(0, 8).map((item) => (
                <div className="home-coverage-row" key={item.region}>
                  <span>{item.region}</span>
                  <i>
                    <b style={{ width: `${(Number(item.conversations || 0) * 100) / maxRegion}%` }} />
                  </i>
                  <strong>{item.conversations}</strong>
                  <small>{item.contributors} contributors</small>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
